import React, { useCallback, useEffect, useRef, useState } from "react";
import { Camera, Link2, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

function useQrScanner(onCode) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const [supported, setSupported] = useState(true);
  const [scanning, setScanning] = useState(false);

  const stop = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    setScanning(false);
  }, []);

  const fail = (msg) => { setSupported(false); toast.error(msg); };

  const loop = (detector) => {
    const tick = async () => {
      const v = videoRef.current;
      if (v && v.readyState === 4) {
        try {
          const codes = await detector.detect(v);
          if (codes.length > 0) { await onCode(codes[0].rawValue); return; }
        } catch (e) { console.debug("QR detect frame failed", e); }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  const start = async () => {
    if (!("BarcodeDetector" in window)) { fail("Live scanning isn't supported on this browser. Paste the code below instead."); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); }
      setScanning(true);
      loop(new window.BarcodeDetector({ formats: ["qr_code"] }));
    } catch (e) {
      console.warn("Camera access failed", e);
      fail("Could not access the camera. Paste the code below instead.");
    }
  };

  return { videoRef, supported, scanning, start, stop };
}

function useLinkLanyard(employeeId, onDone) {
  const [busy, setBusy] = useState(false);
  const link = async (value) => {
    if (!value) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/employees/${employeeId}/identity/assign`, { token_or_url: value });
      toast.success("Lanyard linked to employee");
      onDone(data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return { busy, link };
}

function ManualEntry({ busy, onLink }) {
  const [manual, setManual] = useState("");
  return (
    <div className="mt-3 border-t border-slate-100 pt-3">
      <p className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-400">Or enter the code / URL</p>
      <div className="flex gap-2">
        <Input value={manual} onChange={(e) => setManual(e.target.value)} placeholder="e.g. 7Kx92mQa or https://…/r/7Kx92mQa" data-testid="manual-token" />
        <Button onClick={() => onLink(manual)} disabled={busy || !manual} data-testid="link-token" className="bg-[#4338CA] hover:bg-[#3730A3]"><Link2 className="mr-1 h-4 w-4" /> Link</Button>
      </div>
    </div>
  );
}

export default function ScanLinkDialog({ open, onOpenChange, employeeId, onLinked }) {
  const stopRef = useRef(() => {});
  const { busy, link } = useLinkLanyard(employeeId, (data) => { stopRef.current(); onOpenChange(false); onLinked?.(data); });
  const { videoRef, supported, scanning, start, stop } = useQrScanner(link);
  stopRef.current = stop;

  useEffect(() => { if (!open) stop(); return stop; }, [open, stop]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) stop(); onOpenChange(o); }}>
      <DialogContent>
        <DialogHeader><DialogTitle>Link a pre-printed lanyard</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Scan the QR on an existing printed lanyard to assign it to this employee. Any previous link to that lanyard is replaced.</p>
        <div className="mt-2 overflow-hidden rounded-xl bg-slate-900" style={{ aspectRatio: "4/3" }}>
          {scanning ? (
            <video ref={videoRef} className="h-full w-full object-cover" muted playsInline data-testid="scan-video" />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-slate-400">
              <Camera className="h-10 w-10" />
              <Button onClick={start} data-testid="start-scan" className="bg-[#4338CA] hover:bg-[#3730A3]">Start camera scan</Button>
            </div>
          )}
        </div>
        {scanning && <Button variant="outline" onClick={stop} data-testid="stop-scan" className="mt-2"><XCircle className="mr-1 h-4 w-4" /> Stop camera</Button>}
        {!supported && <p className="rounded-lg bg-amber-50 p-2 text-xs text-amber-800">Live scanning isn't available here — paste the lanyard's code or URL below.</p>}
        <ManualEntry busy={busy} onLink={link} />
      </DialogContent>
    </Dialog>
  );
}
